import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { cachedLiffChatContext, readLiffChatContext, type LiffChatContext } from '@/lib/liff';

/**
 * Tracks "where am I right now?" — personal (PC web / 1:1 chat) vs a specific
 * LINE group/room context (LIFF opened inside that chat). Drives ledger
 * filtering on HomePage and ledger-creation defaults.
 *
 *   - On boot we read the cached value so the UI can render instantly.
 *   - Then `refresh()` calls liff.getContext() to confirm or correct it.
 */
export const useChatContextStore = defineStore('chatContext', () => {
  const context = ref<LiffChatContext>(cachedLiffChatContext());
  const loaded = ref(false);

  const isGroup = computed(() => context.value.type === 'group');
  const lineGroupId = computed(() =>
    context.value.type === 'group' ? context.value.lineGroupId : null,
  );

  /** "personal" or "group:<lineGroupId>" — used as ?context query param on the API. */
  const filter = computed(() =>
    context.value.type === 'group' ? `group:${context.value.lineGroupId}` : 'personal',
  );

  async function refresh() {
    context.value = await readLiffChatContext();
    loaded.value = true;
  }

  return { context, loaded, isGroup, lineGroupId, filter, refresh };
});
